<script setup lang="ts">
import type { DbStockMetricsLight } from '~~/types/stock.types'
import { MagnifyingGlassIcon } from '@heroicons/vue/16/solid'
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core'

const props = withDefaults(defineProps<{
  theme?: 'light' | 'primary'
  isFreemium?: boolean
  disableNavigation?: boolean
  clearOnSelect?: boolean
  multiple?: boolean
  excludeTickers?: string[]
}>(), {
  theme: 'light',
  isFreemium: false,
  disableNavigation: false,
  clearOnSelect: false,
  multiple: false,
  excludeTickers: () => []
})

const emit = defineEmits<{
  'selectedTicker': [value: string]
  'selectedStock': [value: DbStockMetricsLight]
  'notFreemiumAllowed': []
}>()

// v-model support for multiple selection
const modelValue = defineModel<string[] | null>()

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

// Track the search query from VirtualCombobox
const searchQuery = ref('')

// Fetch all stock metrics and filter to allowed companies
const { data: lightStockMetrics } = useSearchStockMetrics()

// Filter out excluded tickers
const filteredStockMetrics = computed(() => {
  if (!lightStockMetrics.value) return null
  if (props.excludeTickers.length === 0) return lightStockMetrics.value
  const excludeSet = new Set(props.excludeTickers)
  return lightStockMetrics.value.filter(stock => !excludeSet.has(stock.ticker_symbol))
})

// Sort and filter stock metrics based on search relevance
const sortedAndFilteredStockMetrics = computed(() => {
  if (!filteredStockMetrics.value) return null
  
  const query = searchQuery.value.toLowerCase().trim()
  
  // If no search query, return all filtered stocks in original order
  if (!query) return filteredStockMetrics.value
  
  // Helper function to extract ticker symbol after the colon
  const extractTickerSymbol = (fullTicker: string): string => {
    const parts = fullTicker.split(':')
    return parts.length > 1 ? parts[1]! : fullTicker
  }
  
  // Separate stocks into priority groups
  const startsWithMatches: DbStockMetricsLight[] = []
  const containsMatches: DbStockMetricsLight[] = []
  
  for (const stock of filteredStockMetrics.value) {
    const nameLower = stock.name.toLowerCase()
    const tickerSymbol = extractTickerSymbol(stock.ticker_symbol).toLowerCase()
    
    // Check if name or ticker starts with the query
    const nameStartsWith = nameLower.startsWith(query)
    const tickerStartsWith = tickerSymbol.startsWith(query)
    
    if (nameStartsWith || tickerStartsWith) {
      startsWithMatches.push(stock)
    } else {
      // Check if name or ticker contains the query
      const nameContains = nameLower.includes(query)
      const tickerContains = tickerSymbol.includes(query)
      
      if (nameContains || tickerContains) {
        containsMatches.push(stock)
      }
    }
  }
  
  // Return priority 1 matches followed by priority 2 matches
  return [...startsWithMatches, ...containsMatches]
})

// Keep track of top 10 companies by market cap
const top10Companies = computed(() => {
  if (!lightStockMetrics.value) return new Set<string>()
  return new Set(lightStockMetrics.value.slice(0, 10).map(stock => stock.ticker_symbol))
})

// Get the current ticker from the route
const route = useRoute()
const currentTicker = computed<string | undefined>(() => route.params?.stockMetricId as string | undefined)

// Selected company handling
const selectedCompany = ref<DbStockMetricsLight | null>(null)
const isNavigating = ref(false)
watch(selectedCompany, async (newCompany) => {
  if (newCompany) {
    // --- Logic for Multiple Selection ---
    if (props.multiple) {
      const currentSelection = modelValue.value ?? [];
      if (!currentSelection.includes(newCompany.ticker_symbol)) {
        modelValue.value = [...currentSelection, newCompany.ticker_symbol];
      }
      // Always clear the input for next selection in multiple mode
      selectedCompany.value = null;
      return; // Stop further processing
    }

    // --- Logic for Single Selection (Potentially Disabled Navigation) ---
    if (props.disableNavigation) {
      emit('selectedStock', newCompany)
      emit('selectedTicker', newCompany.ticker_symbol); // Emit selectedTicker instead
      if (props.clearOnSelect) {
        selectedCompany.value = null; // Clear selection after emitting
      }
      return; // Stop further processing
    }

    // --- Existing Navigation Logic ---
    // Check if company is allowed for freemium users
    if (props.isFreemium && !top10Companies.value.has(newCompany.ticker_symbol)) {
      emit('notFreemiumAllowed')
      selectedCompany.value = null
      return
    }

    // Check if company is the same as the current one
    if (currentTicker.value === newCompany.ticker_symbol) {
      selectedCompany.value = null
      return
    }

    isNavigating.value = true
    try {
      emit('selectedStock', newCompany)
      emit('selectedTicker', newCompany.ticker_symbol)
      await navigateTo(`${props.isFreemium ? '/public' : ''}/company/${newCompany.ticker_symbol}`)
    } catch (error) {
      console.error('Error navigating to company page:', error)
      isNavigating.value = false
      selectedCompany.value = null
    }
  }
})

// Reset selected company after page navigation is complete
const nuxtApp = useNuxtApp()
nuxtApp.hook('page:finish', () => {
  isNavigating.value = false
  selectedCompany.value = null
})

// Simplified filter function - now only used as a safety check since we pre-filter
const filterFunction = (stock: DbStockMetricsLight, query: string): boolean => {
  // Return true for all items since we're handling filtering in sortedAndFilteredStockMetrics
  return true
}

// Function to remove a ticker from the selection (for multiple mode)
const removeTicker = (tickerToRemove: string) => {
  if (props.multiple && modelValue.value) {
    modelValue.value = modelValue.value.filter(ticker => ticker !== tickerToRemove);
  }
}

// Display function to show company name and ticker
const displayValue = (stock: DbStockMetricsLight | null) => {
  if (!stock) return ''
  return `${stock.name} (${stock.ticker_symbol.split(':')[1]})`
}
</script>

<template>
  <div class="w-full relative z-30">
    <VirtualCombobox
      v-model="selectedCompany"
      v-model:query="searchQuery"
      :items="sortedAndFilteredStockMetrics || []"
      :display-value="displayValue"
      :filter-function="filterFunction"
      :estimate-size="50"
      :placeholder="sortedAndFilteredStockMetrics === null ? 'Chargement des entreprises...' : isMobile ? 'Rechercher' : 'Rechercher une entreprise'"
      :disabled="isNavigating || sortedAndFilteredStockMetrics === null"
      :theme="theme"
      :has-prefix="true"
      :has-suffix="false"
      class="cursor-pointer"
    >
      <template #prefix>
        <MagnifyingGlassIcon class="h-5 w-5" :class="theme === 'light' ? 'text-gray-500' : 'text-white'" />
      </template>
      <template #option="{ item, isActive }">
        <div
          v-if="item"
          class="relative cursor-pointer select-none h-12 px-4 flex items-center"
          :class="{
            'bg-sky-blue-400 text-white': isActive,
            'text-gray-900': !isActive,
          }"
        >
          <StockInfos
            :key="item.ticker_symbol"
            :stock="{
              ticker_symbol: item.ticker_symbol,
              name: item.name,
              logo_url: getIconLogoUrl(item.isins[0], item.logo_type, item.ticker_symbol),
            }"
            :show-logo="true"
            :show-exchange="true"
            :is-high="false"
          />
        </div>
      </template>
    </VirtualCombobox>

    <!-- Badge display for multiple selection -->
    <div v-if="multiple && modelValue && modelValue.length > 0" class="flex flex-wrap gap-2 mt-2">
      <Badge
        v-for="ticker in modelValue"
        :key="ticker"
        :has-close-button="true"
        size="sm"
        @close="removeTicker(ticker)"
      >
        {{ ticker }}
      </Badge>
    </div>
  </div>
</template> 
